import { useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation, useNavigationType } from "react-router-dom";
import Lenis from "lenis";

const scrollPositions = new Map();
let scrollSaveFrozenUntil = 0;

const scrollToHashTarget = (target) => {
  let tries = 0;
  const tick = () => {
    const el = document.querySelector(target);
    if (el && window.__lenis) {
      window.__lenis.resize();
      window.__lenis.scrollTo(el, { offset: -72, immediate: true });
      if (Math.abs(el.getBoundingClientRect().top - 72) < 24) {
        window.history.replaceState({}, "");
        return;
      }
    }
    tries += 1;
    if (tries <= 12) setTimeout(tick, 300);
    else window.history.replaceState({}, "");
  };
  setTimeout(tick, 250);
};

const restoreScrollPosition = (y) => {
  let tries = 0;
  let cancelled = false;
  const cancel = () => { cancelled = true; };
  window.addEventListener("wheel", cancel, { once: true, passive: true });
  window.addEventListener("touchstart", cancel, { once: true, passive: true });
  const restore = () => {
    if (cancelled) return;
    if (window.__lenis) {
      window.__lenis.resize();
      window.__lenis.scrollTo(y, { immediate: true });
    } else {
      window.scrollTo(0, y);
    }
    tries += 1;
    if (Math.abs(window.scrollY - y) > 4 && tries < 10) setTimeout(restore, 150);
  };
  setTimeout(restore, 50);
};

// Lenis smooth scroll, touch phantom-tap guard, and per-route scroll restoration.
export const useScrollBehavior = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navType = useNavigationType();
  const locationKeyRef = useRef(location.key);
  locationKeyRef.current = location.key;

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let id;
    const raf = (t) => { lenis.raf(t); id = requestAnimationFrame(raf); };
    id = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(id); lenis.destroy(); window.__lenis = null; };
  }, []);

  useEffect(() => {
    let lastMove = 0;
    let lastTouch = 0;
    const mark = () => { lastMove = Date.now(); };
    const markTouch = () => { lastTouch = Date.now(); };
    const onClickCapture = (e) => {
      const now = Date.now();
      if (now - lastTouch < 700 && now - lastMove < 150 && e.target.closest("a")) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (e.target.closest("a,button")) {
        scrollPositions.set(locationKeyRef.current, window.scrollY);
        scrollSaveFrozenUntil = now + 800;
      }
    };
    window.addEventListener("touchstart", markTouch, { passive: true });
    window.addEventListener("touchmove", mark, { passive: true });
    window.addEventListener("scroll", mark, { passive: true });
    document.addEventListener("click", onClickCapture, true);
    return () => {
      window.removeEventListener("touchstart", markTouch);
      window.removeEventListener("touchmove", mark);
      window.removeEventListener("scroll", mark);
      document.removeEventListener("click", onClickCapture, true);
    };
  }, []);

  useEffect(() => {
    window.history.scrollRestoration = "manual";
    const save = () => {
      if (Date.now() > scrollSaveFrozenUntil) scrollPositions.set(location.key, window.scrollY);
    };
    window.addEventListener("scroll", save, { passive: true });
    return () => window.removeEventListener("scroll", save);
  }, [location.key]);

  useEffect(() => {
    const target = location.state?.scrollTo;
    if (location.pathname === "/" && target) {
      scrollToHashTarget(target);
    } else if (navType === "POP" && scrollPositions.has(location.key)) {
      restoreScrollPosition(scrollPositions.get(location.key));
    } else if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.state, location.key, navType]);

  const scrollTo = useCallback((id) => {
    if (window.location.pathname !== "/") {
      navigate("/", { state: { scrollTo: id } });
      return;
    }
    const el = document.querySelector(id);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
    else el.scrollIntoView({ behavior: "smooth" });
  }, [navigate]);

  return scrollTo;
};
